import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthFacade } from '../../auth/state/auth.facade';

export type LocationPermissionState = 'granted' | 'denied' | 'prompt' | 'unsupported';

@Injectable({
	providedIn: 'root'
})
export class LocationService {

	private readonly permissionStateSubject = new BehaviorSubject<LocationPermissionState>('prompt');
	permissionState$: Observable<LocationPermissionState> = this.permissionStateSubject.asObservable();

	constructor(private authFacade: AuthFacade) {
		this.refreshPermissionState();
	}

	async refreshPermissionState(): Promise<void> {
		try {
			const status = await Geolocation.checkPermissions();
			this.permissionStateSubject.next(status.location as LocationPermissionState);
		} catch {
			this.permissionStateSubject.next('unsupported');
		}
	}

	// Re-prompts for location permission and waits for the user's actual decision before resolving.
	// Native: Geolocation.requestPermissions() shows the real OS dialog directly. Web: that method
	// isn't implemented by Capacitor's web shim (throws 'unimplemented' - see its web.js), so this
	// falls back to actually fetching a position, which is what triggers the browser's own prompt and
	// only resolves/rejects once the user has answered it.
	async requestPermission(): Promise<LocationPermissionState> {
		try {
			await Geolocation.requestPermissions();
		} catch {
			try {
				await Geolocation.getCurrentPosition({ timeout: 10000 });
			} catch {
				// Denied or timed out - refreshPermissionState() below reads the real outcome either way.
			}
		}

		await this.refreshPermissionState();
		if (this.permissionStateSubject.value === 'granted') {
			this.authFacade.updateCoordinates();
		}
		return this.permissionStateSubject.value;
	}
}
